import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  FunctionField,
  EditButton,
  ShowButton,
  DeleteButton,
  useTranslate,
  Filter,
  CreateButton,
  TopToolbar,
  ExportButton,
  RefreshButton,
  SelectInput,
  TextInput,
  BooleanInput,
  useRecordContext,
  SearchInput,
  useListContext,
  RecordContextProvider,
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Tooltip,
  Fab,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  LocalOffer,
  Schedule,
  Check,
  Close,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const wrappedChipSx = {
  maxWidth: '100%',
  minWidth: 0,
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
    py: 0.5,
  },
};

const PromotionsFilter = (props: any) => {
  const translate = useTranslate();

  return (
    <Filter {...props}>
      <TextInput
        label={translate('resources.promotions.fields.name')}
        source="q"
        alwaysOn
      />
      <SelectInput
        source="promotionType"
        label={translate('resources.promotions.fields.promotionType')}
        choices={[
          { id: 'coupon', name: translate('resources.promotions.types.coupon') },
          { id: 'time_limited', name: translate('resources.promotions.types.time_limited') },
        ]}
      />
      <SelectInput
        source="codeType"
        label={translate('resources.promotions.fields.codeType')}
        choices={[
          { id: 'none', name: translate('resources.promotions.codeTypes.none') },
          { id: 'shared', name: translate('resources.promotions.codeTypes.shared') },
          { id: 'unique', name: translate('resources.promotions.codeTypes.unique') },
        ]}
      />
      <SelectInput
        source="discountType"
        label={translate('resources.promotions.fields.discountType')}
        choices={[
          { id: 'percentage', name: translate('resources.promotions.discountTypes.percentage') },
          { id: 'fixed_amount', name: translate('resources.promotions.discountTypes.fixed_amount') },
          { id: 'free_credit', name: translate('resources.promotions.discountTypes.free_credit') },
        ]}
      />
      <SelectInput
        source="userType"
        label={translate('resources.promotions.fields.userType')}
        choices={[
          { id: 'app', name: translate('resources.promotions.userTypes.app') },
          { id: 'rfid', name: translate('resources.promotions.userTypes.rfid') },
          { id: 'all', name: translate('resources.promotions.userTypes.all') },
        ]}
      />
      <BooleanInput
        source="isActive"
        label={translate('resources.promotions.fields.isActive')}
      />
    </Filter>
  );
};

const MobilePromotionsFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
  </Filter>
);

const DesktopListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
    <RefreshButton />
  </TopToolbar>
);

const MobileListActions = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1, flexWrap: 'wrap', width: '100%' }}>
    <RefreshButton />
    <ExportButton />
  </Box>
);

const StatusChip = ({ status }: { status: string }) => {
  const translate = useTranslate();
  let color: 'default' | 'primary' | 'success' | 'error' | 'warning' = 'default';
  let icon;

  switch (status) {
    case 'scheduled':
      color = 'warning';
      icon = <Schedule fontSize="small" />;
      break;
    case 'active':
      color = 'success';
      icon = <Check fontSize="small" />;
      break;
    case 'expired':
      color = 'error';
      icon = <Close fontSize="small" />;
      break;
    case 'inactive':
      color = 'default';
      break;
  }

  return (
    <Chip
      label={translate(`resources.promotions.status.${status}`)}
      color={color}
      size="small"
      icon={icon}
      sx={wrappedChipSx}
    />
  );
};

const getRecordStatus = (record: any) => {
  const now = new Date();
  const validFrom = new Date(record.valid_from);
  const validUntil = new Date(record.valid_until);
  let status = 'inactive';
  if (record.is_active) {
    if (now < validFrom) {
      status = 'scheduled';
    } else if (now >= validFrom && now <= validUntil) {
      status = 'active';
    } else {
      status = 'expired';
    }
  }
  return status;
};

const getDiscountDisplay = (record: any) => {
  const value = record.discount_value;
  if (record.discount_type === 'percentage') {
    return `${value}%`;
  } else if (record.discount_type === 'fixed_amount') {
    return `HKD ${value}`;
  } else if (record.discount_type === 'free_credit') {
    return `HKD ${value}`;
  }
  return '';
};

const formatCompactDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
};

const MobileList = () => {
  const { data, isLoading } = useListContext();
  const translate = useTranslate();
  const navigate = useNavigate();

  if (isLoading || !data) return null;

  return (
    <Stack spacing={1}>
      {data.map((record: any) => (
        <RecordContextProvider key={record.id} value={record}>
          <Box
            onClick={() => navigate(`/promotions/${record.id}/show`)}
            sx={{
              p: 1.5,
              backgroundColor: '#fff',
              borderRadius: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              '&:active': { backgroundColor: '#f5f5f5' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              {record.promotion_type === 'coupon' ? (
                <LocalOffer sx={{ fontSize: 16, color: 'text.secondary' }} />
              ) : (
                <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {record.name}
              </Typography>
              <StatusChip status={getRecordStatus(record)} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#00C853' }}>
                {getDiscountDisplay(record)}
              </Typography>
              <Chip
                label={translate(`resources.promotions.userTypes.${record.user_type || 'all'}`)}
                size="small"
                color={record.user_type === 'all' ? 'default' : 'primary'}
                sx={wrappedChipSx}
              />
              <Typography variant="caption" color="text.secondary">
                {record.current_uses || 0}{record.max_uses_total ? `/${record.max_uses_total}` : ''} uses
              </Typography>
              <Typography variant="caption" color="text.secondary">
                #{record.priority}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              {formatCompactDate(record.valid_from)} - {formatCompactDate(record.valid_until)}
            </Typography>
          </Box>
        </RecordContextProvider>
      ))}
    </Stack>
  );
};

export const PromotionsList = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  return (
    <Box sx={{ position: 'relative' }}>
      <List
        filters={isMobile ? <MobilePromotionsFilter /> : <PromotionsFilter />}
        actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
        empty={isMobile ? false : undefined}
        perPage={25}
        sort={{ field: 'priority', order: 'ASC' }}
        sx={{
          maxWidth: '100%',
          '& .RaList-main, & .RaList-content, & .RaList-actions, & .RaList-noResults': {
            maxWidth: '100%',
            minWidth: 0,
          },
          '& .RaList-content': {
            backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
            maxWidth: '100%',
          },
          ...(isMobile && {
            '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
            '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important', width: '100% !important', maxWidth: '100% !important', minWidth: '0 !important' },
            '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important', width: '100% !important', maxWidth: '100% !important', minWidth: '0 !important', px: '0 !important' },
            '& .RaListToolbar-root > span': { display: 'none' },
            '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
            '& .RaListToolbar-root > form, & .RaFilterForm-root': {
              display: 'flex !important',
              flexWrap: 'wrap !important',
              flex: '1 1 100% !important',
              width: '100% !important',
              maxWidth: '100% !important',
              minWidth: '0 !important',
              margin: '0 !important',
            },
            '& .RaFilterForm-spacer': { display: 'none !important' },
            '& .filter-field, & .RaFilterForm-filterFormInput, & .RaFilterForm-input': {
              flex: '1 1 100% !important',
              width: '100% !important',
              maxWidth: '100% !important',
              minWidth: '0 !important',
              marginRight: '0 !important',
            },
            '& .filter-field .MuiFormControl-root, & .RaFilterForm-input .MuiFormControl-root': {
              width: '100% !important',
              maxWidth: '100% !important',
              minWidth: '0 !important',
            },
            '& .filter-field .MuiInputBase-root, & .RaFilterForm-input .MuiInputBase-root': {
              width: '100% !important',
              maxWidth: '100% !important',
              minWidth: '0 !important',
            },
            '& .filter-field .MuiInputBase-input, & .RaFilterForm-input .MuiInputBase-input': {
              minWidth: '0 !important',
            },
            '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important', px: '0 !important', width: '100% !important', maxWidth: '100% !important' },
          }),
        }}
      >
        {isMobile ? (
          <MobileList />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick="show"
            sx={{
              '& .RaDatagrid-headerCell': {
                backgroundColor: '#F5F5F5',
                fontWeight: 'bold',
              },
              '& .RaDatagrid-row': {
                '&:hover': {
                  backgroundColor: '#F0F7FF',
                },
              },
              '& .column-type': { width: '120px' },
              '& .column-name': { minWidth: '200px', flex: 1 },
              '& .column-code': { width: '120px' },
              '& .column-discount': { width: '100px' },
              '& .column-user_type': { width: '90px' },
              '& .column-validity': { width: '140px' },
              '& .column-status': { width: '100px' },
              '& .column-usage': { width: '100px' },
              '& .column-priority': { width: '110px' },
              '& .column-actions': { width: '120px' },
            }}
          >
            <FunctionField
              label={translate('resources.promotions.fields.type')}
              render={(record: any) => (
                <Chip
                  label={translate(`resources.promotions.types.${record.promotion_type}`)}
                  icon={record.promotion_type === 'coupon' ? <LocalOffer fontSize="small" /> : <Schedule fontSize="small" />}
                  size="small"
                  variant="outlined"
                />
              )}
              source="type"
            />

            <TextField
              source="name"
              label={translate('resources.promotions.fields.name')}
              sx={{
                '& .MuiTypography-root': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
              }}
            />

            <FunctionField
              label={translate('resources.promotions.fields.code')}
              render={(record: any) => {
                if (record.code) {
                  return (
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                    >
                      {record.code}
                    </Typography>
                  );
                }
                return <Typography variant="body2" color="text.secondary">-</Typography>;
              }}
              source="code"
            />

            <FunctionField
              label={translate('resources.promotions.fields.discount')}
              render={(record: any) => {
                const display = getDiscountDisplay(record);
                return (
                  <Tooltip title={translate(`resources.promotions.discountTypes.${record.discount_type}`)}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#00C853' }}>
                      {display}
                    </Typography>
                  </Tooltip>
                );
              }}
              source="discount"
            />

            <FunctionField
              label={translate('resources.promotions.fields.userType')}
              render={(record: any) => (
                <Chip
                  label={translate(`resources.promotions.userTypes.${record.user_type || 'all'}`)}
                  size="small"
                  color={record.user_type === 'all' ? 'default' : 'primary'}
                />
              )}
              source="user_type"
            />

            <FunctionField
              label={translate('resources.promotions.sections.validity')}
              render={(record: any) => {
                const from = new Date(record.valid_from);
                const until = new Date(record.valid_until);

                return (
                  <Box>
                    <Typography variant="caption" display="block">
                      {from.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      to {until.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                    </Typography>
                  </Box>
                );
              }}
              source="validity"
            />

            <FunctionField
              label={translate('resources.promotions.fields.status')}
              render={(record: any) => <StatusChip status={getRecordStatus(record)} />}
              source="status"
            />

            <FunctionField
              label={translate('resources.promotions.fields.uses')}
              render={(record: any) => {
                const current = record.current_uses || 0;
                const max = record.max_uses_total;

                return (
                  <Tooltip title={max ? `${current} / ${max} uses` : `${current} uses (unlimited)`}>
                    <Typography variant="body2">
                      {current}
                      {max && <Typography component="span" variant="caption" color="text.secondary">/{max}</Typography>}
                    </Typography>
                  </Tooltip>
                );
              }}
              source="usage"
            />

            <NumberField
              source="priority"
              label={translate('resources.promotions.fields.priority')}
            />

            <FunctionField
              label=""
              render={(record: any) => (
                <Box display="flex" gap={0}>
                  <Tooltip title={translate('ra.action.show')}>
                    <span>
                      <ShowButton record={record} size="small" label="" />
                    </span>
                  </Tooltip>
                  <Tooltip title={translate('ra.action.edit')}>
                    <span>
                      <EditButton record={record} size="small" label="" />
                    </span>
                  </Tooltip>
                  <Tooltip title={translate('ra.action.delete')}>
                    <span>
                      <DeleteButton record={record} size="small" label="" />
                    </span>
                  </Tooltip>
                </Box>
              )}
              source="actions"
              textAlign="right"
            />
          </Datagrid>
        )}
      </List>
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/promotions/create')}
          sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};
